/**
 * 区分や種類をポップなピルで選ばせる部品。タスク詳細で使う。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 区分（大分類・事業・中分類）や種類を、パステルのピルの見た目で選ばせる部品
 * @graph-connects none
 */
import { kindColor } from './KindLabel';

/**
 * PillSelect の props。
 */
export interface PillSelectProps {
  /** 読み上げ用の名前 */
  label: string;
  /** 選んでいる値。空なら未選択 */
  value: string;
  /** 未選択のときの表示 */
  emptyLabel: string;
  /** 未選択を選べるか。必須の区分（事業）では選ばせない */
  allowEmpty?: boolean;
  /** 選択肢 */
  options: readonly string[];
  /** 塗る色。未選択のときは渡さない（点線の枠になる） */
  color?: string;
  /** 選び直したときに呼ばれる */
  onChange: (value: string) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 区分をポップなピルの見た目のプルダウンで選ばせる。未選択は点線の枠にして、選べることが分かるようにする
 * @graph-connects none
 */
export function PillSelect({
  label,
  value,
  emptyLabel,
  allowEmpty = true,
  options,
  color,
  onChange,
}: PillSelectProps) {
  return (
    <span
      className={`relative inline-flex items-center rounded-full shadow-sm ${
        color === undefined
          ? 'border border-dashed border-[var(--color-border-subtle)] text-[var(--color-text-muted)]'
          : 'text-[var(--color-pill-text)]'
      }`}
      style={color === undefined ? undefined : { backgroundColor: pastel(color) }}
    >
      <select
        aria-label={label}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="cursor-pointer appearance-none rounded-full bg-transparent py-1 pr-6 pl-3 font-semibold"
      >
        {allowEmpty || value === '' ? <option value="">{emptyLabel}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-2 text-[9px] opacity-70"
      >
        ▼
      </span>
    </span>
  );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 区分のピルの色を白で薄めてパステルにする。色相は一覧の大分類・事業の色のまま保つ
 * @graph-connects none
 */
export function pastel(color: string): string {
  return `color-mix(in srgb, ${color} 55%, white)`;
}

/** 作るときに必ず出す種類。設定や既存のタスクに無くても選べるようにする */
/** @graph-connects none */
const BASE_KINDS: readonly string[] = ['月次経理', '決算', '会議'];

/**
 * KindPicker の props。
 */
export interface KindPickerProps {
  /** 選んでいる種類 */
  value: string;
  /** 設定や既存のタスクにある種類 */
  kinds: readonly string[];
  /** 選び直したときに呼ばれる */
  onChange: (kind: string) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 新しく作るタスクの種類を、パステルのピルで選ばせる。月次経理・決算・会議を先頭に並べる
 * @graph-connects kindColor [calls] 種類の色を引く
 * @graph-connects pastel [calls] 色をパステルにする
 */
export function KindPicker({ value, kinds, onChange }: KindPickerProps) {
  const options = [...new Set([...BASE_KINDS, ...kinds].filter((name) => name !== ''))];
  return (
    <div role="radiogroup" aria-label="タスクの種類" className="mb-3 flex flex-wrap gap-1.5">
      {options.map((name) => (
        <button
          key={name}
          type="button"
          role="radio"
          aria-checked={value === name}
          onClick={() => {
            onChange(name);
          }}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            value === name
              ? 'text-[var(--color-pill-text)] shadow-sm'
              : 'border border-[var(--color-border-subtle)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
          }`}
          style={value === name ? { backgroundColor: pastel(kindColor(name)) } : undefined}
        >
          {name}
        </button>
      ))}
    </div>
  );
}
